<template>
  <main class="layoutList">
    <section class="layoutList-scroll" :style="{ height: contentHeight }">
      <section ref="layoutCams">
        <section class="layoutList-layout">
          <h4 class="layoutList-title">表示しているカメラ</h4>

          <draggable
            class="layoutList-layers"
            :class="{
              ['layoutList-layers--text']: !displayThumbnail,
              ['layoutList-layers--video']: displayThumbnail,
            }"
            :style="{ height: layersHeight }"
            :list="initLayers"
            group="cams"
            handle=".icon-arrange"
            :move="handleMove"
            @end="handleDragEnd"
          >
            <div
              v-for="(cam, index) in initLayers"
              :key="index"
              class="layoutList-layer"
            >
              <span class="icon icon-arrange"></span>
              <h5>
                <video-stream
                  v-if="displayThumbnail"
                  :id="initLayers[index].id"
                  :ref="`display-layer${index}`"
                  :key="`main-cam${index}`"
                  :stream-config="{
                    accessKeyId: iamKey,
                    secretAccessKey: iamSecret,
                    streamName: initLayers[index].kvsVideoStreamName,
                    region: initLayers[index].awsRegionName,
                    autoplay: true,
                  }"
                  :title="initLayers[index].name"
                  main
                  :disable-zoom="true"
                  zoomout-on-restore
                  show-skeleton
                  controller-size="small"
                  :show-controllers="false"
                  :hide-display-controller="true"
                />
                <span class="layoutList-layer_title">{{ cam.name }}</span>
              </h5>
            </div>
          </draggable>
          <h4 class="layoutList-title layoutList-cam_title">非表示のカメラ</h4>
        </section>
        <draggable
          class="layoutList-cams"
          :class="{
            ['layoutList-cams--text']: !displayThumbnail,
            ['layoutList-cams--video']: displayThumbnail,
          }"
          :list="initCams"
          group="cams"
          handle=".icon-arrange"
          :move="handleMove"
          @end="handleDragEnd"
        >
          <div
            v-for="(cam, index) in initCams"
            :key="index"
            class="layoutList-cam"
          >
            <span class="icon icon-arrange"></span>
            <h5>
              <video-stream
                v-if="displayThumbnail"
                :id="initCams[index].id"
                :ref="`display-cam-${index}`"
                :region="initCams[index].awsRegionName"
                :stream-config="{
                  accessKeyId: iamKey,
                  secretAccessKey: iamSecret,
                  streamName: initCams[index].kvsVideoStreamName,
                  region: initCams[index].awsRegionName,
                  autoplay: true,
                }"
                :title="initCams[index].name"
                main
                :disable-zoom="true"
                zoomout-on-restore
                show-skeleton
                controller-size="small"
                :show-controllers="false"
                :hide-display-controller="true"
              />
              <span class="layoutList-cam_title">{{ cam.name }}</span>
            </h5>
          </div>
        </draggable>
        <div class="layoutIcons-footer_gap"></div>
      </section>
    </section>
  </main>
</template>

<script>
import draggable from 'vuedraggable';
import { mapState } from 'vuex';
import VideoStream from './VideoStream.vue';

export default {
  name: 'LayoutList',
  components: {
    draggable,
    VideoStream,
  },
  props: {
    contentHeight: {
      type: String,
      default: 'auto',
    },
    cams: {
      type: Array,
      required: true,
    },
    layers: {
      type: Array,
      required: true,
    },
    displayThumbnail: {
      type: Boolean,
      required: false,
    },
  },
  data() {
    return {
      initCams: this.cams,
      initLayers: this.layers,
      movedTo: '',
      movedFrom: '',
      movingIndex: 0,
      futureIndex: 0,
    };
  },
  computed: {
    ...mapState({
      iamKey: (state) => state.common.iam.accessKeyId,
      iamSecret: (state) => state.common.iam.secretAccessKey,
    }),
    layersHeight() {
      const multiplier = this.displayThumbnail ? 87 : 50;
      return (
        (this.initLayers.length > 2
          ? 3 * multiplier
          : (this.initLayers.length + 1) * multiplier) + 'px'
      );
    },
  },
  watch: {
    initCams(value) {
      this.handleChange(value, 'cams');
    },
    initLayers(value) {
      this.handleChange(value, 'layers');
    },
  },
  mounted() {
    this.$emit('mounted');
  },
  methods: {
    handleChange(value, type) {
      this.$emit('change', { value, type });
    },
    handleDragEnd() {
      if (this.movedFrom === 'secondary' && this.movedTo === 'main') {
        // moved from secondary to main
        const draggedElementValue = this.initCams[this.movingIndex];
        const mainElementValue = this.initLayers[this.futureIndex];
        if (this.initLayers.length === 3) {
          this.$set(this.initCams, this.movingIndex, { ...mainElementValue });
          this.$set(this.initLayers, this.futureIndex, {
            ...draggedElementValue,
          });
        } else {
          this.initCams.splice(this.movingIndex, 1);
          this.initLayers.splice(this.futureIndex, 0, draggedElementValue);
        }
      } else if (this.movedFrom === 'main' && this.movedTo === 'secondary') {
        // moved from main to secondary
        const draggedElementValue = this.initLayers[this.movingIndex];
        const mainElementValue = this.initCams[this.futureIndex];
        this.$set(this.initCams, this.futureIndex, { ...draggedElementValue });
        this.$set(this.initLayers, this.movingIndex, {
          ...mainElementValue,
        });
      } else if (
        this.movedFrom === 'secondary' &&
        this.movedTo === 'secondary'
      ) {
        // moved from secondary to secondary
        const draggedElementValue = this.initCams[this.movingIndex];
        const secondaryElementValue = this.initCams[this.futureIndex];
        this.$set(this.initCams, this.movingIndex, {
          ...secondaryElementValue,
        });
        this.$set(this.initCams, this.futureIndex, { ...draggedElementValue });
      } else if (this.movedFrom === 'main' && this.movedTo === 'main') {
        // moved from main to main
        const draggedElementValue = this.initLayers[this.movingIndex];
        const mainElementValue = this.initLayers[this.futureIndex];
        this.$set(this.initLayers, this.movingIndex, { ...mainElementValue });
        this.$set(this.initLayers, this.futureIndex, {
          ...draggedElementValue,
        });
      }

      this.movedFrom = '';
      this.movedTo = '';
    },
    handleMove(e) {
      const { to, from, draggedContext } = e;
      if (to.className.indexOf('layoutList-layers') > -1) {
        this.movedTo = 'main';
      } else if (to.className.indexOf('layoutList-cams') > -1) {
        this.movedTo = 'secondary';
      } else {
        this.movedTo = '';
      }
      if (from.className.indexOf('layoutList-layers') > -1) {
        this.movedFrom = 'main';
      } else if (from.className.indexOf('layoutList-cams') > -1) {
        this.movedFrom = 'secondary';
      } else {
        this.movedFrom = '';
      }

      const { index, futureIndex } = draggedContext;
      this.movingIndex = index;
      this.futureIndex = futureIndex;
      return false;
    },
  },
};
</script>

<style lang="scss">
.layoutList {
  width: 495px;
  border: solid 1px #ccc;
  margin: 0 auto;
  background-color: #eeeeee;
  position: relative;
  &-scroll {
    overflow-y: auto;
    z-index: 1;
  }
  &-gap {
    height: 230px;
  }
  &-title {
    background-color: #f3f3f3;
    height: 40px;
    font-size: 14px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    padding: 10px 13px;
    letter-spacing: normal;
    text-align: left;
    color: #4b4b4b;
    border-bottom: solid 1px #ccc;
  }
  &-layout {
    position: sticky;
    top: 0;
    left: 0;
    width: 100%;
    background-color: #eeeeee;
    z-index: 2;
  }
  &-layers,
  &-cams {
    min-height: 50px;
    &--text {
      .layoutList-layer,
      .layoutList-cam {
        display: flex;
        align-items: center;
      }
    }
    &--video {
      .layoutList-layer,
      .layoutList-cam {
        height: 87px;
        display: flex;
        align-items: center;
      }
    }
  }
  &-layers {
    &--video {
      height: 264px;
    }
  }
  &-layer,
  &-cam {
    background-color: #fff;
    border-bottom: solid 1px #ccc;
    height: 50px;
    .videoStream {
      margin-right: 25px;
      height: 66px;
      video {
        width: 117px;
        height: 66px;
        object-fit: cover;
      }
    }
    &_title {
      font-size: 14px;
      font-weight: bold;
      font-stretch: normal;
      font-style: normal;
      line-height: 1.5;
      letter-spacing: normal;
      text-align: left;
      color: #4b4b4b;
    }

    & > h5 {
      display: flex;
      align-items: center;
      padding-left: 10px;
      border-left: solid 1px #ccc;
      height: 100%;
    }
    .icon.icon-arrange {
      padding: 13px;
      font-size: 24px;
      color: #adadad;
      cursor: move;
    }
  }
  &-footer_gap {
    height: 76px;
  }
}
</style>
